import React, { useState, useEffect } from "react";
import { UserX } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usuariosAPI, type Usuario } from "@/lib/api";
import { cn } from "@/lib/utils";
import { ListNewLoading, ListToolbar } from "@/components/ListNewButton";

interface Props {
  onEdit?: (item: Usuario) => void;
  onNew?: () => void;
}

function usuarioIsActive(user: Usuario): boolean {
  if (typeof user.activo === "boolean") return user.activo;
  if (typeof user.isActive === "boolean") return user.isActive;
  if (typeof user.is_active === "boolean") return user.is_active;
  return true;
}

export function UsuariosListado({ onEdit, onNew }: Props) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showInactivos, setShowInactivos] = useState(false);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUsuarios = async () => {
      try {
        const data = await usuariosAPI.list();
        setUsuarios(data);
      } catch (error) {
        console.error("Error loading usuarios:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchUsuarios();
  }, []);

  const filteredUsuarios = usuarios.filter((u) => {
    if (!showInactivos && !usuarioIsActive(u)) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      u.codigo?.toLowerCase().includes(q) ||
      u.username?.toLowerCase().includes(q) ||
      u.nombre?.toLowerCase().includes(q) ||
      u.apellido?.toLowerCase().includes(q) ||
      u.telefono?.includes(searchQuery) ||
      u.email?.toLowerCase().includes(q) ||
      u.rol?.toLowerCase().includes(q)
    );
  });

  if (loading) {
    return <ListNewLoading onNew={onNew} message="Cargando usuarios..." />;
  }

  const toggleLabel = showInactivos ? "Ocultar inactivos" : "Mostrar inactivos";

  return (
    <div className="space-y-4 mt-4">
      <ListToolbar
        onNew={onNew}
        extra={
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className={cn(
                    "h-9 w-9 shrink-0",
                    showInactivos
                      ? "bg-accent text-accent-foreground"
                      : "text-muted-foreground",
                  )}
                  aria-pressed={showInactivos}
                  aria-label={toggleLabel}
                  onClick={() => setShowInactivos((v) => !v)}
                >
                  <UserX className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{toggleLabel}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        }
      >
        <label className="block text-sm font-medium mb-1">Buscar</label>
        <input
          type="text"
          placeholder="Buscar por cualquier campo..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
        />
      </ListToolbar>

      {filteredUsuarios.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground">
          No se encontraron usuarios
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-2 text-left">Código</th>
                <th className="px-4 py-2 text-left">Usuario</th>
                <th className="px-4 py-2 text-left">Nombre Completo</th>
                <th className="px-4 py-2 text-left">Teléfono</th>
                <th className="px-4 py-2 text-left">Email</th>
                <th className="px-4 py-2 text-left">Rol</th>
                <th className="px-4 py-2 text-left">Estado</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsuarios.map((usuario) => {
                const active = usuarioIsActive(usuario);
                return (
                <tr
                  key={usuario.codigo || usuario.id || usuario.username}
                  className={`border-b hover:bg-muted/50 cursor-pointer ${active ? "" : "text-muted-foreground"}`}
                  onClick={() => onEdit?.(usuario)}
                >
                  <td className="px-4 py-2">{usuario.codigo || '-'}</td>
                  <td className="px-4 py-2">{usuario.username}</td>
                  <td className="px-4 py-2">{`${usuario.nombre || ''} ${usuario.apellido || ''}`.trim() || '-'}</td>
                  <td className="px-4 py-2">{usuario.telefono || '-'}</td>
                  <td className="px-4 py-2">{usuario.email || '-'}</td>
                  <td className="px-4 py-2">{usuario.rol ? usuario.rol.charAt(0).toUpperCase() + usuario.rol.slice(1) : 'Sin rol'}</td>
                  <td className="px-4 py-2">
                    {active ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                        Activo
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                        Inactivo
                      </span>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
